/**
 * Add Citation Modal Component
 * 
 * Allows users to add new citations to report sections by selecting artifacts
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Button, Input, Badge, Card } from '../UI';
import { useWorkspace } from '../../hooks/useWorkspace';
import { useConfig } from '../../contexts/ConfigContext';
import { searchArtifacts } from '../../api/artifacts';
import { RegistryFilterBar } from '../Knowledge/RegistryFilterBar';
import { RegistryMetaBadges } from '../Knowledge/RegistryMetaBadges';
import { useRegistryArtifactFilters } from '../../hooks/useRegistryArtifactFilters';
import type { Artifact } from '../../types/api';
import {
  Search,
  FileText,
  CheckCircle,
  X,
  Loader2,
  Plus,
  ChevronDown,
  HelpCircle,
  Upload,
  Mail,
  AlertCircle,
} from 'lucide-react';

interface AddCitationModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportId: string;
  sectionId?: string;
  sectionName?: string;
  report?: any; // Report object with sections
  onSuccess?: () => void;
}

export function AddCitationModal({
  isOpen,
  onClose,
  reportId,
  sectionId: initialSectionId,
  sectionName: initialSectionName,
  report,
  onSuccess,
}: AddCitationModalProps) {
  const { projectId } = useWorkspace();
  const config = useConfig();
  const supportEmail = config?.customization?.supportEmail;
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [selectedArtifactIds, setSelectedArtifactIds] = useState<Set<string>>(new Set());
  const [isAdding, setIsAdding] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState<string | undefined>(initialSectionId);
  const [selectedSectionName, setSelectedSectionName] = useState<string | undefined>(initialSectionName);
  const [currentPage, setCurrentPage] = useState(0);
  const [allArtifacts, setAllArtifacts] = useState<Artifact[]>([]); // Accumulated artifacts
  const pageSize = 50; // Load 50 at a time
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadErrors, setUploadErrors] = useState<Record<string, string>>({});

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(0); // Reset to first page on new search
      setAllArtifacts([]); // Clear accumulated results on new search
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch artifacts with server-side search and pagination
  const { data: artifactsData, isLoading, isFetching, error } = useQuery({
    queryKey: ['artifacts-search', projectId, debouncedSearchQuery, currentPage],
    queryFn: async () => {
      if (!projectId) return { artifacts: [], total: 0, has_more: false };
      
      // Use search endpoint with filters
      // Note: We filter API integrations client-side since the API source_type filter
      // doesn't support exclusion patterns (only exact match or wildcard)
      const response = await searchArtifacts({
        project_id: projectId,
        query: debouncedSearchQuery || undefined, // Empty string = no search, undefined = all
        limit: pageSize,
        offset: currentPage * pageSize,
      });
      
      // Ensure response has the expected structure
      return {
        artifacts: response.artifacts || [],
        total: response.total || 0,
        has_more: response.has_more || false,
        limit: response.limit || pageSize,
        offset: response.offset || 0,
        error: response.error, // Pass through any error message
      };
    },
    enabled: isOpen && !!projectId,
    staleTime: 30 * 1000, // Cache for 30 seconds
    retry: 2, // Retry up to 2 times on failure
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff: 1s, 2s, max 5s
  });

  // Filter out API integration sources from results
  // This is done client-side because the API source_type filter only supports
  // exact match or wildcard patterns, not exclusion
  const nonApiArtifacts = React.useMemo(() => {
    if (!artifactsData?.artifacts || !Array.isArray(artifactsData.artifacts)) {
      return [];
    }
    
    return artifactsData.artifacts.filter((artifact: any) => {
      // Only filter out if source_type explicitly indicates API integration
      // Don't filter based on filename patterns alone - too many false positives
      if (artifact.source_type && typeof artifact.source_type === 'string') {
        // Only exclude if source_type starts with 'api_integration'
        if (artifact.source_type.startsWith('api_integration')) {
          return false;
        }
        // If source_type is 'general_reference' or other non-API types, include it
        return true;
      }
      
      // If no source_type, check metadata for integration_type
      if (artifact.meta && typeof artifact.meta === 'object' && artifact.meta.integration_type) {
        return false;
      }
      
      // Default: include the artifact (be permissive)
      return true;
    });
  }, [artifactsData?.artifacts]);
  
  // Debug logging (development only)
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development' && artifactsData) {
      console.log('[AddCitationModal] Artifacts data:', {
        total: artifactsData.total,
        artifactsCount: artifactsData.artifacts?.length || 0,
        nonApiCount: nonApiArtifacts.length,
        hasMore: artifactsData.has_more,
        currentPage,
      });
      if (artifactsData.artifacts && artifactsData.artifacts.length > 0) {
        console.log('[AddCitationModal] Sample artifact:', artifactsData.artifacts[0]);
      }
    }
  }, [artifactsData, nonApiArtifacts.length, currentPage]);

  // Accumulate artifacts across pages
  useEffect(() => {
    if (currentPage === 0) {
      // First page - replace all
      setAllArtifacts(nonApiArtifacts);
    } else {
      // Subsequent pages - append
      setAllArtifacts(prev => {
        // Avoid duplicates
        const existingIds = new Set(prev.map((a: any) => a.id));
        const newArtifacts = nonApiArtifacts.filter((a: any) => !existingIds.has(a.id));
        return [...prev, ...newArtifacts];
      });
    }
  }, [nonApiArtifacts, currentPage]);

  const {
    filters: registryFilters,
    setFilters: setRegistryFilters,
    groupBy,
    setGroupBy,
    options: registryFilterOptions,
    registryArtifactCount,
    filteredArtifacts,
    clearFilters: clearRegistryFilters,
  } = useRegistryArtifactFilters(allArtifacts);

  const hasMore = artifactsData?.has_more === true && artifactsData.artifacts?.length === pageSize;
  // For total count, use the API total
  const totalCount = artifactsData?.total || 0;
  
  // Get available sections from report
  const availableSections = React.useMemo(() => {
    if (!report?.sections || !Array.isArray(report.sections)) return [];
    return report.sections.map((s: any) => ({
      id: s.id,
      name: s.section_name,
      question: s.question,
    }));
  }, [report?.sections]);
  
  // Initialize section selection
  React.useEffect(() => {
    if (availableSections.length > 0 && !selectedSectionId) {
      // Default to first section if none selected
      setSelectedSectionId(availableSections[0].id);
      setSelectedSectionName(availableSections[0].name);
    }
  }, [availableSections, selectedSectionId]);

  // Add evidence mutation
  // Note: The add_evidence endpoint expects file uploads, not artifact_ids
  // For adding existing artifacts, we need to use a different approach
  // We'll create a new endpoint call that adds artifacts to sections
  const addEvidenceMutation = useMutation({
    mutationFn: async (artifactIds: string[]) => {
      if (!selectedSectionId) {
        throw new Error('Section ID is required to add citations');
      }
      const { regenerateSections } = await import('../../api/reports');
      return await regenerateSections(reportId, {
        section_ids: [selectedSectionId],
        excluded_artifact_ids: [],
        included_artifact_ids: artifactIds,
        fast_mode: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      setSelectedArtifactIds(new Set());
      setSearchQuery('');
      onSuccess?.();
      onClose();
    },
  });

  const handleToggleArtifact = (artifactId: string) => {
    setSelectedArtifactIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(artifactId)) {
        newSet.delete(artifactId);
      } else {
        newSet.add(artifactId);
      }
      return newSet;
    });
  };

  const handleAddCitations = async () => {
    if (selectedArtifactIds.size === 0) {
      alert('Please select at least one artifact to add as a citation');
      return;
    }

    if (!selectedSectionId) {
      alert('Please select a section first.');
      return;
    }

    setIsAdding(true);
    try {
      const response = await addEvidenceMutation.mutateAsync(Array.from(selectedArtifactIds));
      alert(`Successfully added ${selectedArtifactIds.size} citation(s) to ${selectedSectionName || 'the section'}. The section has been regenerated with these new sources.\n\nNew stats:\n- Sections regenerated: ${response.sections_regenerated}\n- Total citations: ${response.new_stats?.total_citations || 'N/A'}`);
    } catch (error: any) {
      console.error('Failed to add citations:', error);
      const detail =
        error?.response?.data?.detail ??
        (typeof error?.response?.data === 'string' ? error.response.data : null);
      const serverMsg =
        typeof detail === 'string'
          ? detail
          : detail?.message || (Array.isArray(detail) ? detail.map((d: any) => d?.msg || d).join(' ') : '');
      const msg = serverMsg || error.message || 'Unknown error';
      alert(
        `Failed to add citations: ${msg}\n\nNote: Adding citations regenerates the section using the selected documents. If you use dev mode over a private IP, the API URL must point to the backend (port 8000), not the Vite port.`
      );
    } finally {
      setIsAdding(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    setUploadingFiles(fileArray);
    setUploadProgress({});
    setUploadErrors({});

    // Import uploadArtifact dynamically
    const { uploadArtifact } = await import('../../api/artifacts');

    // Upload each file
    for (const file of fileArray) {
      try {
        setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
        
        await uploadArtifact(
          projectId,
          file,
          (progress) => {
            setUploadProgress(prev => ({ ...prev, [file.name]: progress }));
          },
          undefined, // source_id - will create "upload" source automatically
          'general_reference', // source_type
          false // forceReupload
        );

        setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
      } catch (error: any) {
        console.error(`Failed to upload ${file.name}:`, error);
        setUploadErrors(prev => ({ 
          ...prev, 
          [file.name]: error.message || 'Upload failed' 
        }));
      }
    }

    // Refresh artifacts list after uploads
    queryClient.invalidateQueries({ queryKey: ['artifacts-search', projectId] });
    
    // Show success message
    const successCount = fileArray.length - Object.keys(uploadErrors).length;
    if (successCount > 0) {
      alert(`Successfully uploaded ${successCount} file(s). They will appear in the list below after ingestion completes.`);
    }
    
    // Clear upload state after a delay
    setTimeout(() => {
      setShowUploadModal(false);
      setUploadingFiles([]);
      setUploadProgress({});
      setUploadErrors({});
    }, 2000);
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Citations to Report"
      size="xl"
    >
      <div className="space-y-4">
        {/* Help Text - Can't Find Artifact */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <HelpCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="font-medium text-blue-900 dark:text-blue-300 mb-2">
                Can't find the document you're looking for?
              </p>
              <div className="space-y-2 text-blue-800 dark:text-blue-400">
                {supportEmail && (
                <div className="flex items-start gap-2">
                  <Mail className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <p>
                    <strong>Company documents:</strong> Email{' '}
                    <a
                      href={`mailto:${supportEmail}?subject=Request to sync SharePoint folder`}
                      className="underline hover:text-blue-600 dark:hover:text-blue-300"
                    >
                      {supportEmail}
                    </a>{' '}
                    to request auto-sync of a SharePoint/OneDrive/Drive folder that hasn't been synced yet.
                  </p>
                </div>
                )}
                <div className="flex items-start gap-2">
                  <Upload className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <p>
                    <strong>Personal or external documents:</strong>{' '}
                    <button
                      onClick={() => setShowUploadModal(true)}
                      className="underline hover:text-blue-600 dark:hover:text-blue-300 font-medium"
                    >
                      Upload files here
                    </button>{' '}
                    to add individual files. They'll be organized into a source automatically (not creating a new source per file).
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Upload Modal Section */}
        {showUploadModal && (
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                <h3 className="font-medium text-gray-900 dark:text-gray-100">Upload Files</h3>
              </div>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadingFiles([]);
                  setUploadProgress({});
                  setUploadErrors({});
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {uploadingFiles.length === 0 ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Upload documents to add them to this project. They'll be automatically indexed and available for citations.
                </p>
                <label className="block">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    className="block w-full text-sm text-gray-500 dark:text-gray-400
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-lg file:border-0
                      file:text-sm file:font-semibold
                      file:bg-blue-50 file:text-blue-700
                      hover:file:bg-blue-100
                      dark:file:bg-blue-900/20 dark:file:text-blue-400
                      dark:hover:file:bg-blue-900/30
                      cursor-pointer"
                    accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.txt,.pptx,.ppt"
                  />
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  Supported formats: PDF, Word, Excel, PowerPoint, CSV, TXT
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {uploadingFiles.map((file) => {
                  const progress = uploadProgress[file.name] || 0;
                  const error = uploadErrors[file.name];
                  
                  return (
                    <div key={file.name} className="bg-white dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {file.name}
                          </span>
                        </div>
                        {error ? (
                          <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                        ) : progress === 100 ? (
                          <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                        ) : (
                          <Loader2 className="h-4 w-4 animate-spin text-blue-500 flex-shrink-0" />
                        )}
                      </div>
                      {error ? (
                        <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
                      ) : (
                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        
        {/* Section Selector */}
        {availableSections.length > 0 && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <label className="block text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
              Select Section to Add Citations:
            </label>
            <select
              value={selectedSectionId || ''}
              onChange={(e) => {
                const section = availableSections.find(s => s.id === e.target.value);
                setSelectedSectionId(e.target.value);
                setSelectedSectionName(section?.name);
              }}
              className="w-full px-3 py-2 border border-blue-200 dark:border-blue-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {availableSections.map((section) => (
                <option key={section.id} value={section.id}>
                  {section.name}
                </option>
              ))}
            </select>
            {selectedSectionName && (
              <p className="text-xs text-blue-700 dark:text-blue-400 mt-2">
                Selected artifacts will be added as citations to <strong>{selectedSectionName}</strong>. The section will be regenerated to include content from these sources.
              </p>
            )}
          </div>
        )}
        
        {availableSections.length === 0 && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
            <p className="text-sm text-yellow-900 dark:text-yellow-300">
              ⚠️ No sections available. Please ensure the report has sections before adding citations.
            </p>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search artifacts by filename, path, or metadata..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
          {isFetching && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            </div>
          )}
        </div>

        {registryArtifactCount > 0 && (
          <RegistryFilterBar
            filters={registryFilters}
            options={registryFilterOptions}
            groupBy={groupBy}
            registryArtifactCount={registryArtifactCount}
            onFiltersChange={setRegistryFilters}
            onGroupByChange={setGroupBy}
            onClear={clearRegistryFilters}
          />
        )}
        
        {/* Search Results Info */}
        {debouncedSearchQuery && (
          <div className="text-xs text-gray-600 dark:text-gray-400">
            {filteredArtifacts.length > 0 ? (
              <>
                Showing {filteredArtifacts.length} artifact{filteredArtifacts.length !== 1 ? 's' : ''} matching "{debouncedSearchQuery}"
                {hasMore && (
                  <> (more available - click "Load More")</>
                )}
              </>
            ) : !isLoading ? (
              <>No artifacts found matching "{debouncedSearchQuery}"</>
            ) : null}
          </div>
        )}
        
        {!debouncedSearchQuery && filteredArtifacts.length > 0 && (
          <div className="text-xs text-gray-600 dark:text-gray-400">
            Showing {filteredArtifacts.length} artifact{filteredArtifacts.length !== 1 ? 's' : ''}
            {hasMore && <> (more available - click "Load More")</>}
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-sm text-red-900 dark:text-red-300">
              Error loading artifacts: {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </div>
        )}

        {/* Artifact List */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-96 overflow-y-auto">
          {isLoading && currentPage === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600 dark:text-gray-400">
                {debouncedSearchQuery ? 'Searching artifacts...' : 'Loading artifacts...'}
              </span>
            </div>
          ) : filteredArtifacts.length === 0 && !isLoading && !error ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                {debouncedSearchQuery 
                  ? `No artifacts found matching "${debouncedSearchQuery}"` 
                  : 'No artifacts available in this project'}
              </p>
              {debouncedSearchQuery && (
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                  Try a different search term or clear the search to see all artifacts
                </p>
              )}
              {!debouncedSearchQuery && (
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                  Upload artifacts to your project to add them as citations
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredArtifacts.map((artifact: any) => {
                  const isSelected = selectedArtifactIds.has(artifact.id);
                  return (
                    <div
                      key={artifact.id}
                      className={`p-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer ${
                        isSelected ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500' : ''
                      }`}
                      onClick={() => handleToggleArtifact(artifact.id)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-1">
                          {isSelected ? (
                            <CheckCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          ) : (
                            <div className="h-5 w-5 rounded-full border-2 border-gray-300 dark:border-gray-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                              {artifact.filename || 'Unknown File'}
                            </span>
                            {artifact.artifact_type && (
                              <Badge variant="outline" className="text-xs">
                                {artifact.artifact_type.toUpperCase()}
                              </Badge>
                            )}
                          </div>
                          {artifact.id && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-mono truncate">
                              ID: {artifact.id}
                            </p>
                          )}
                          {artifact.file_size_bytes && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Size: {(artifact.file_size_bytes / 1024 / 1024).toFixed(2)} MB
                            </p>
                          )}
                          <RegistryMetaBadges artifact={artifact} compact />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Load More Button */}
              {hasMore && (
                <div className="p-3 border-t border-gray-200 dark:border-gray-700">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    disabled={isFetching}
                    className="w-full flex items-center justify-center gap-2"
                  >
                    {isFetching ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4" />
                        Load More ({filteredArtifacts.length} of {totalCount} shown)
                      </>
                    )}
                  </Button>
                </div>
              )}
              
              {!hasMore && filteredArtifacts.length > 0 && (
                <div className="p-2 text-center text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
                  Showing all {filteredArtifacts.length} artifact{filteredArtifacts.length !== 1 ? 's' : ''}
                </div>
              )}
            </>
          )}
        </div>

        {/* Selection Summary */}
        {selectedArtifactIds.size > 0 && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
            <p className="text-sm text-green-900 dark:text-green-300">
              <strong>{selectedArtifactIds.size}</strong> artifact{selectedArtifactIds.size !== 1 ? 's' : ''} selected
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={onClose} disabled={isAdding}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddCitations}
            disabled={selectedArtifactIds.size === 0 || isAdding || !selectedSectionId || availableSections.length === 0}
            className="flex items-center gap-2"
          >
            {isAdding ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Adding...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" />
                Add {selectedArtifactIds.size > 0 ? `${selectedArtifactIds.size} ` : ''}Citation{selectedArtifactIds.size !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
